import { z } from "zod";

/**
 * Which Meilisearch fields carry which product attributes — every tenant's
 * index has different field names, so nothing here can be hardcoded (PLAN.md
 * "Per-tenant Meilisearch schemas differ"). `url` is optional because not
 * every catalogue stores a product URL; see `productUrlTemplate` below.
 */
export const meilisearchFieldMappingSchema = z.object({
  /** Which field carries the document's id. Defaults to `id`/`objectID`/`productId` when unset — some indexes hide their primary key from `displayedAttributes`, so a tenant can point this at whatever field the index does return (e.g. `slug`, `item_no`). */
  id: z.string().min(1).optional(),
  title: z.string().min(1),
  price: z.string().min(1),
  image: z.string().min(1).optional(),
  stock: z.string().min(1).optional(),
  url: z.string().min(1).optional(),
});
export type MeilisearchFieldMapping = z.infer<typeof meilisearchFieldMappingSchema>;

/**
 * PLAN.md "Product URLs may be templated rather than stored": a catalogue
 * without a stored URL field can still produce a working link via a
 * `{item_no}`-style template filled from the hydrated, mapped record.
 * Exactly one of `fieldMapping.url` or `productUrlTemplate` must be set —
 * enforced below so the ambiguity can never reach the tool at read time.
 */
function requireExactlyOneUrlSource(
  value: { fieldMapping: MeilisearchFieldMapping; productUrlTemplate?: string },
  ctx: z.RefinementCtx,
): void {
  const hasUrlField = Boolean(value.fieldMapping.url);
  const hasTemplate = Boolean(value.productUrlTemplate);
  if (hasUrlField === hasTemplate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: hasUrlField
        ? "set either fieldMapping.url or productUrlTemplate, not both"
        : "set either fieldMapping.url (the index field holding the product URL) or productUrlTemplate (e.g. https://shop.example.com/product/{item_no})",
      path: hasUrlField ? ["productUrlTemplate"] : ["fieldMapping", "url"],
    });
  }
}

/**
 * Unlike the URL, an image is optional (a card without a picture is still
 * a card), so this only rejects the ambiguous case — both set — rather
 * than requiring either. Same reasoning as the URL though: `fieldMapping`
 * fields often hold a path relative to the catalogue's own host (a
 * Meilisearch index storing `/media/sku-123.jpg`), which a shopper's
 * browser on a client's site would resolve against the client's own
 * origin, not the shop's — `productImageTemplate` (e.g.
 * `https://cdn.shop.example.com{image_path}`) is what turns that back into
 * an absolute URL, the same way `productUrlTemplate` does for links.
 */
function forbidBothImageSources(
  value: { fieldMapping: MeilisearchFieldMapping; productImageTemplate?: string },
  ctx: z.RefinementCtx,
): void {
  if (value.fieldMapping.image && value.productImageTemplate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "set either fieldMapping.image or productImageTemplate, not both",
      path: ["productImageTemplate"],
    });
  }
}

/**
 * `.trim()` on all three, because every one of them is pasted: a URL that
 * arrives as `"https://search.example.com "` and a key that arrives with
 * the newline a terminal added are the same typo, and neither is visible
 * in a form field. Trimming at the schema means the stored value is the
 * usable one, rather than every consumer re-trimming (and one forgetting).
 */
const meilisearchConfigShape = {
  apiUrl: z.string().trim().url(),
  apiKey: z.string().trim().min(1),
  indexName: z.string().trim().min(1),
  fieldMapping: meilisearchFieldMappingSchema,
  productUrlTemplate: z.string().min(1).optional(),
  productImageTemplate: z.string().min(1).optional(),
  currency: z.string().length(3).default("USD"),
};

function validateMeilisearchConfig(
  value: { fieldMapping: MeilisearchFieldMapping; productUrlTemplate?: string; productImageTemplate?: string },
  ctx: z.RefinementCtx,
): void {
  requireExactlyOneUrlSource(value, ctx);
  forbidBothImageSources(value, ctx);
}

/** The complete, stored config — `apiKey` is always present once persisted. */
export const meilisearchIntegrationConfigSchema = z.object(meilisearchConfigShape).superRefine(validateMeilisearchConfig);
export type MeilisearchIntegrationConfig = z.infer<typeof meilisearchIntegrationConfigSchema>;

/**
 * What an admin may submit. `apiKey` is optional here and *only* here: the
 * panel never reads a secret back, so an edit that changes only the index
 * name leaves the key field blank. The route fills it from the stored config
 * and re-validates against the complete schema above, so a first-time save
 * with no key is still rejected.
 */
export const meilisearchIntegrationConfigInputSchema = z
  .object({ ...meilisearchConfigShape, apiKey: meilisearchConfigShape.apiKey.optional() })
  .superRefine(validateMeilisearchConfig);
export type MeilisearchIntegrationConfigInput = z.infer<typeof meilisearchIntegrationConfigInputSchema>;

const qdrantConfigShape = {
  apiUrl: z.string().trim().url(),
  apiKey: z.string().trim().min(1),
  collectionName: z.string().trim().min(1),
  topK: z.number().int().positive().max(20).default(5),
  scoreThreshold: z.number().min(0).max(1).default(0.5),
};

export const qdrantIntegrationConfigSchema = z.object(qdrantConfigShape);
export type QdrantIntegrationConfig = z.infer<typeof qdrantIntegrationConfigSchema>;

/** Same write-only-secret rule as `meilisearchIntegrationConfigInputSchema`. */
export const qdrantIntegrationConfigInputSchema = z.object({ ...qdrantConfigShape, apiKey: qdrantConfigShape.apiKey.optional() });
export type QdrantIntegrationConfigInput = z.infer<typeof qdrantIntegrationConfigInputSchema>;

/**
 * Where a confirmed order goes (PLAN.md "Order and enquiry mail is internal
 * mail with the customer on `Reply-To`"). `recipients` are the org's own
 * sales addresses — never a customer address, which arrives on `Reply-To`
 * instead so sales can just hit reply.
 */
export const orderEmailIntegrationConfigSchema = z.object({
  recipients: z.array(z.string().email()).min(1).max(10),
  /** Shown as the sender's display name; the address stays ours in v1. */
  fromName: z.string().min(1).max(120).optional(),
  subjectPrefix: z.string().max(60).optional(),
});
export type OrderEmailIntegrationConfig = z.infer<typeof orderEmailIntegrationConfigSchema>;

export const integrationKindSchema = z.enum(["meilisearch", "qdrant", "order_email"]);
export type IntegrationKind = z.infer<typeof integrationKindSchema>;

/**
 * What the admin sends to configure an integration — credentials included,
 * write-only (see `integrationDtoSchema`, which never echoes `apiKey`).
 * Uses the input schemas, so `apiKey` may be omitted to keep the stored one.
 */
export const upsertIntegrationBodySchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("meilisearch"), config: meilisearchIntegrationConfigInputSchema }),
  z.object({ kind: z.literal("qdrant"), config: qdrantIntegrationConfigInputSchema }),
  z.object({ kind: z.literal("order_email"), config: orderEmailIntegrationConfigSchema }),
]);
export type UpsertIntegrationBody = z.infer<typeof upsertIntegrationBodySchema>;

/**
 * What the admin panel reads back — same "sets, never reads back a secret"
 * rule as `provider_credentials` (PLAN.md), so `apiKey` never appears here.
 * `apiUrl` isn't sensitive and is included so the admin can see which
 * instance is configured without re-entering everything to change one field.
 */
export const integrationDtoSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("meilisearch"),
    id: z.string().uuid(),
    apiUrl: z.string(),
    indexName: z.string(),
    /**
     * Whether a credential is stored — never the credential. "Is a key set
     * at all" is the first question a failing connection raises, and
     * without it the panel's blank, write-only key field looks identical
     * whether one is stored or not.
     */
    hasApiKey: z.boolean(),
    fieldMapping: meilisearchFieldMappingSchema,
    productUrlTemplate: z.string().optional(),
    productImageTemplate: z.string().optional(),
    currency: z.string(),
    lastVerifiedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
  }),
  z.object({
    kind: z.literal("qdrant"),
    id: z.string().uuid(),
    apiUrl: z.string(),
    collectionName: z.string(),
    hasApiKey: z.boolean(),
    topK: z.number(),
    scoreThreshold: z.number(),
    lastVerifiedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
  }),
  z.object({
    kind: z.literal("order_email"),
    id: z.string().uuid(),
    // Recipients are the org's own addresses and the panel needs to show
    // them to be usable — unlike an API key, there is nothing secret here.
    recipients: z.array(z.string()),
    fromName: z.string().optional(),
    subjectPrefix: z.string().optional(),
    lastVerifiedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
  }),
]);
export type IntegrationDto = z.infer<typeof integrationDtoSchema>;

/**
 * Optional overrides for a connection test. Testing only what is *stored*
 * makes the commonest failure impossible to diagnose: the admin cannot tell
 * whether the key they just typed is wrong or whether the one on file is,
 * because the panel never reads either back. Anything omitted here falls
 * back to the stored config, so `{}` is still "test what is saved".
 */
export const testIntegrationBodySchema = z.object({
  config: z.record(z.unknown()).optional(),
});
export type TestIntegrationBody = z.infer<typeof testIntegrationBodySchema>;

export const testIntegrationResponseSchema = z.object({ ok: z.boolean(), message: z.string().optional() });
export type TestIntegrationResponse = z.infer<typeof testIntegrationResponseSchema>;

/**
 * The overview's search health check: for each search backend the org has
 * connected, whether it answers right now and how many items agents can
 * draw on. Readable by every member (unlike the integrations themselves,
 * which are platform-admin only) — it carries counts and a status, never a
 * URL or key.
 */
export const searchHealthSourceSchema = z.object({
  kind: z.enum(["meilisearch", "qdrant"]),
  status: z.enum(["ok", "error"]),
  /** Null when the backend couldn't be reached. */
  count: z.number().int().nonnegative().nullable(),
  /** Why it failed, or — for an ok source — a caveat worth showing. */
  message: z.string().optional(),
});
export type SearchHealthSource = z.infer<typeof searchHealthSourceSchema>;

export const searchHealthDtoSchema = z.object({ sources: z.array(searchHealthSourceSchema) });
export type SearchHealthDto = z.infer<typeof searchHealthDtoSchema>;
